import { z } from 'zod';
import { SiteSchema } from './site.js';
import { SHIFTBOOK_ENTRY_PERMISSION_ACTIONS } from '../permissions/shiftbook-entry.js';
export const ShiftbookEntryTypeSchema = z.enum(['suggestion', 'instruction', 'record']);
export type ShiftbookEntryType = z.infer<typeof ShiftbookEntryTypeSchema>;

export const ShiftbookEntryStatusSchema = z.enum(['open', 'in_progress', 'completed']);
export type ShiftbookEntryStatus = z.infer<typeof ShiftbookEntryStatusSchema>;
export const ShiftbookEntryWorkflowStatusSchema = z.enum(['open', 'in_progress', 'completed']);
export type ShiftbookEntryWorkflowStatus = z.infer<typeof ShiftbookEntryWorkflowStatusSchema>;

export const ShiftbookEntryPrioritySchema = z.enum(['normal', 'medium', 'high']);
export type ShiftbookEntryPriority = z.infer<typeof ShiftbookEntryPrioritySchema>;

export const ShiftbookEntryScheduleStateSchema = z.enum(['none', 'overdue', 'due_today']);
export type ShiftbookEntryScheduleState = z.infer<typeof ShiftbookEntryScheduleStateSchema>;

export const ShiftbookEntryTitleSchema = z
  .string()
  .trim()
  .min(1, 'Title must not be empty')
  .max(255, 'Title must not exceed 255 characters');
export type ShiftbookEntryTitle = z.infer<typeof ShiftbookEntryTitleSchema>;

export const ShiftbookEntryDescriptionSchema = z
  .string()
  .trim()
  .min(1, 'Description must not be empty')
  .max(4000, 'Description must not exceed 4000 characters');
export type ShiftbookEntryDescription = z.infer<typeof ShiftbookEntryDescriptionSchema>;

export const SuggestionKapTagIdSchema = z.string().uuid('Invalid suggestion KAP tag ID');
export type SuggestionKapTagId = z.infer<typeof SuggestionKapTagIdSchema>;

export const SuggestionKapTagNumberSchema = z
  .string()
  .trim()
  .min(1, 'Suggestion KAP number must not be empty')
  .max(32, 'Suggestion KAP number must not exceed 32 characters');
export type SuggestionKapTagNumber = z.infer<typeof SuggestionKapTagNumberSchema>;

export const SuggestionKapTagDescriptionSchema = z
  .string()
  .trim()
  .min(1, 'Suggestion KAP description must not be empty')
  .max(255, 'Suggestion KAP description must not exceed 255 characters');
export type SuggestionKapTagDescription = z.infer<typeof SuggestionKapTagDescriptionSchema>;

export const ShiftbookEntryKeywordValueSchema = z
  .string()
  .trim()
  .min(1, 'Keyword must not be empty')
  .max(255, 'Keyword must not exceed 255 characters');
export type ShiftbookEntryKeywordValue = z.infer<typeof ShiftbookEntryKeywordValueSchema>;

export const ShiftbookKeywordIdSchema = z.string().uuid('Invalid keyword ID');
export type ShiftbookKeywordId = z.infer<typeof ShiftbookKeywordIdSchema>;

export const ShiftbookKeywordSelectionSchema = z
  .object({
    keywordId: ShiftbookKeywordIdSchema,
    value: ShiftbookEntryKeywordValueSchema,
    isActive: z.boolean(),
    isInScope: z.boolean(),
  })
  .strict();
export type ShiftbookKeywordSelection = z.infer<typeof ShiftbookKeywordSelectionSchema>;

const ShiftbookEntryAreaSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const ShiftbookEntryEquipmentSchema = z
  .object({
    id: z.string().uuid(),
    kapNumber: z.string(),
    label: z.string(),
  })
  .nullable();

const ShiftbookEntryActorSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
});

export const ShiftbookResponsiblePartySchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('user'),
      id: z.string().uuid(),
      displayName: z.string(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('group'),
      id: z.string().uuid(),
      displayName: z.string(),
      memberCount: z.number().int().nonnegative(),
    })
    .strict(),
]);
export type ShiftbookResponsibleParty = z.infer<typeof ShiftbookResponsiblePartySchema>;

export const ShiftbookResponsiblePartyReferenceSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('user'),
      id: z.string().uuid('Invalid responsible user ID'),
    })
    .strict(),
  z
    .object({
      kind: z.literal('group'),
      id: z.string().uuid('Invalid responsible group ID'),
    })
    .strict(),
]);
export type ShiftbookResponsiblePartyReference = z.infer<
  typeof ShiftbookResponsiblePartyReferenceSchema
>;

const ShiftbookEntryCreatedViaActorSchema = ShiftbookEntryActorSchema.nullable().optional();
export const ShiftbookEntryTemplateRevisionIdSchema = z.string().uuid();
export type ShiftbookEntryTemplateRevisionId = z.infer<
  typeof ShiftbookEntryTemplateRevisionIdSchema
>;
export const ShiftbookEntryLegacyHeadingSchema = ShiftbookEntryTitleSchema;
export type ShiftbookEntryLegacyHeading = z.infer<typeof ShiftbookEntryLegacyHeadingSchema>;
export const ShiftbookEntryTemplateResolutionSchema = z.enum(['resolved', 'legacy_unresolved']);
export type ShiftbookEntryTemplateResolution = z.infer<
  typeof ShiftbookEntryTemplateResolutionSchema
>;

export const ShiftbookEntryListItemSchema = z.object({
  id: z.string().uuid(),
  entryNumber: z.number().int().positive(),
  site: SiteSchema.optional(),
  flocId: z.string().uuid(),
  templateRevisionId: ShiftbookEntryTemplateRevisionIdSchema.nullable(),
  legacyHeading: ShiftbookEntryLegacyHeadingSchema.nullable(),
  displayHeading: ShiftbookEntryTitleSchema,
  templateResolution: ShiftbookEntryTemplateResolutionSchema,
  description: z.string().optional(),
  status: ShiftbookEntryStatusSchema,
  type: ShiftbookEntryTypeSchema,
  priority: ShiftbookEntryPrioritySchema,
  area: ShiftbookEntryAreaSchema,
  equipment: ShiftbookEntryEquipmentSchema.optional(),
  suggestionKapTagId: SuggestionKapTagIdSchema.nullable().optional(),
  suggestionKapTagNumber: SuggestionKapTagNumberSchema.nullable().optional(),
  suggestionKapTagDescription: SuggestionKapTagDescriptionSchema.nullable().optional(),
  createdBy: ShiftbookEntryActorSchema.optional(),
  createdAt: z.string().datetime().optional(),
  assignedTo: ShiftbookResponsiblePartySchema.nullable().optional(),
  updatedBy: ShiftbookEntryActorSchema.nullable().optional(),
  dueAt: z.string().datetime().nullable().optional(),
  scheduleState: ShiftbookEntryScheduleStateSchema.optional(),
  keywords: z.array(ShiftbookEntryKeywordValueSchema).default([]),
  lastUpdated: z.string().datetime(),
  hasAttachments: z.boolean().optional(),
  hasComments: z.boolean().optional(),
  attachmentCount: z.number().int().nonnegative().optional(),
  commentCount: z.number().int().nonnegative().optional(),
});
export type ShiftbookEntryListItem = z.infer<typeof ShiftbookEntryListItemSchema>;

export const ShiftbookEntryListResponseSchema = z.object({
  items: z.array(ShiftbookEntryListItemSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});
export type ShiftbookEntryListResponse = z.infer<typeof ShiftbookEntryListResponseSchema>;

export const ShiftbookEntryQueryParamsSchema = z
  .object({
    search: z
      .string()
      .trim()
      .refine(
        (value) => value.length >= 2 || /^#?\d$/.test(value),
        'Search must be at least 2 characters unless searching by entry number',
      )
      .optional(),
    site: z.array(SiteSchema).optional(),
    flocId: z.array(z.string().uuid()).optional(),
    status: z.array(ShiftbookEntryWorkflowStatusSchema).optional(),
    type: z.array(ShiftbookEntryTypeSchema).optional(),
    priority: z.array(ShiftbookEntryPrioritySchema).optional(),
    assignedTo: z
      .array(z.string().uuid())
      .optional()
      .describe(
        'User IDs matched by direct responsibility or membership in the responsible group (RP-3).',
      ),
    assignedToGroup: z.array(z.string().uuid()).optional(),
    createdBy: z.array(z.string().uuid()).optional(),
    keywordIds: z.array(ShiftbookKeywordIdSchema).optional(),
    hasAttachments: z.boolean().optional(),
    hasComments: z.boolean().optional(),
    createdAtFrom: z.string().datetime().optional(),
    createdAtTo: z.string().datetime().optional(),
    dueAtFrom: z.string().datetime().optional(),
    dueAtTo: z.string().datetime().optional(),
    chronologyAsOf: z.string().datetime().optional(),
    showFuture: z.boolean().default(false),
    sortField: z
      .enum(['chronology', 'lastUpdated', 'priority', 'dueDate', 'createdAt', 'assignedTo'])
      .default('chronology'),
    sortDirection: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(10).max(100).default(25),
  })
  .strict();
export type ShiftbookEntryQueryParams = z.infer<typeof ShiftbookEntryQueryParamsSchema>;

export const ShiftbookEntryCreateRequestBaseSchema = z
  .object({
    site: SiteSchema,
    flocId: z.string().uuid('Invalid FLOC ID'),
    type: ShiftbookEntryTypeSchema,
    status: ShiftbookEntryWorkflowStatusSchema.default('open'),
    priority: ShiftbookEntryPrioritySchema.default('normal'),
    templateRevisionId: ShiftbookEntryTemplateRevisionIdSchema.nullable().optional(),
    description: ShiftbookEntryDescriptionSchema,
    responsible: ShiftbookResponsiblePartyReferenceSchema.nullable(),
    dueAt: z.string().datetime('Invalid due date').nullable().optional(),
    suggestionKapTagId: SuggestionKapTagIdSchema.optional(),
  })
  .strict();

export const ShiftbookEntryCreateRequestSchema = ShiftbookEntryCreateRequestBaseSchema.superRefine(
  (value, ctx) => {
    if (value.type !== 'suggestion' && value.suggestionKapTagId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['suggestionKapTagId'],
        message: 'Suggestion KAP tag IDs are only allowed for suggestion entries',
      });
    }
  },
);
export type ShiftbookEntryCreateRequest = z.infer<typeof ShiftbookEntryCreateRequestSchema>;

export const ShiftbookEntryPatchBaseSchema = z
  .object({
    flocId: z.string().uuid('Invalid FLOC ID').optional(),
    type: ShiftbookEntryTypeSchema.optional(),
    priority: ShiftbookEntryPrioritySchema.optional(),
    templateRevisionId: ShiftbookEntryTemplateRevisionIdSchema.nullable().optional(),
    description: ShiftbookEntryDescriptionSchema.optional(),
    responsible: ShiftbookResponsiblePartyReferenceSchema.nullable().optional(),
    dueAt: z.string().datetime('Invalid due date').nullable().optional(),
    suggestionKapTagId: SuggestionKapTagIdSchema.nullable().optional(),
  })
  .strict();

export const ShiftbookEntryPatchSchema = ShiftbookEntryPatchBaseSchema.superRefine((value, ctx) => {
  if (value.suggestionKapTagId && value.type !== 'suggestion') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['suggestionKapTagId'],
      message: 'Non-null suggestion KAP tag IDs require the patch type to be set to suggestion',
    });
  }
});
export type ShiftbookEntryPatch = z.infer<typeof ShiftbookEntryPatchSchema>;

export const ShiftbookEntryUpdateRequestSchema = z
  .object({
    version: z.number().int().positive(),
    patch: ShiftbookEntryPatchSchema,
  })
  .strict()
  .refine((payload) => Object.keys(payload.patch).length > 0, {
    message: 'Patch must include at least one editable field',
    path: ['patch'],
  });
export type ShiftbookEntryUpdateRequest = z.infer<typeof ShiftbookEntryUpdateRequestSchema>;

export const AttachmentThumbnailStatusSchema = z.enum([
  'none',
  'pending',
  'processing',
  'ready',
  'failed',
]);
export type AttachmentThumbnailStatus = z.infer<typeof AttachmentThumbnailStatusSchema>;

export const AttachmentMetadataSchema = z.object({
  source: z.enum(['stored', 'legacy_reference']).default('stored'),
  legacyPath: z.string().nullable().default(null),
  id: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  type: z.string(),
  size: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedById: z.string(),
  uploadedAt: z.string().datetime(),
  scanStatus: z.enum(['pending', 'clean', 'infected', 'error']),
  extractedText: z.string().nullable(),
  thumbnailStatus: AttachmentThumbnailStatusSchema,
  thumbnailMimeType: z.string().nullable(),
  thumbnailWidth: z.number().int().positive().nullable(),
  thumbnailHeight: z.number().int().positive().nullable(),
  thumbnailUpdatedAt: z.string().datetime().nullable(),
});
export type AttachmentMetadata = z.infer<typeof AttachmentMetadataSchema>;

export const AttachmentListResponseSchema = z.array(AttachmentMetadataSchema);
export type AttachmentListResponse = z.infer<typeof AttachmentListResponseSchema>;

export const AttachmentUploadResponseSchema = AttachmentMetadataSchema;
export type AttachmentUploadResponse = z.infer<typeof AttachmentUploadResponseSchema>;

export const AttachmentThumbnailUpdatedEventSchema = z.object({
  entryId: z.string().uuid(),
  attachmentId: z.string().uuid(),
  thumbnailStatus: AttachmentThumbnailStatusSchema,
  thumbnailMimeType: z.string().nullable(),
  thumbnailWidth: z.number().int().positive().nullable(),
  thumbnailHeight: z.number().int().positive().nullable(),
  thumbnailUpdatedAt: z.string().datetime().nullable(),
});
export type AttachmentThumbnailUpdatedEvent = z.infer<typeof AttachmentThumbnailUpdatedEventSchema>;

export const ShiftbookNotificationEventTypeSchema = z.enum([
  'shiftbook.entry.created',
  'shiftbook.entry.updated',
  'shiftbook.entry.deleted',
]);
export type ShiftbookNotificationEventType = z.infer<typeof ShiftbookNotificationEventTypeSchema>;

export const ShiftbookNotificationEventSchema = z.object({
  id: z.string().uuid(),
  type: ShiftbookNotificationEventTypeSchema,
  site: SiteSchema,
  entryId: z.string().uuid(),
  entryTitle: z.string().min(1),
  actorId: z.string().min(1),
  actorDisplayName: z.string().min(1),
  createdAt: z.string().datetime(),
});
export type ShiftbookNotificationEvent = z.infer<typeof ShiftbookNotificationEventSchema>;

export const ShiftbookChecklistItemResponseSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  isDone: z.boolean(),
  order: z.number().int().nonnegative().optional(),
});
export type ShiftbookChecklistItemResponse = z.infer<typeof ShiftbookChecklistItemResponseSchema>;

export const ShiftbookCommentResponseSchema = z.object({
  id: z.string().uuid(),
  author: ShiftbookEntryActorSchema,
  body: z.string(),
  createdAt: z.string().datetime(),
});
export type ShiftbookCommentResponse = z.infer<typeof ShiftbookCommentResponseSchema>;

export const ShiftbookEntryPermissionActionSchema = z.enum(SHIFTBOOK_ENTRY_PERMISSION_ACTIONS);
export type ShiftbookEntryPermissionAction = z.infer<typeof ShiftbookEntryPermissionActionSchema>;

export const ShiftbookEntryPermissionsSchema = z
  .object({
    canEditDetails: z.boolean(),
    canManageChecklist: z.boolean(),
    canManageAttachments: z.boolean(),
    canComment: z.boolean(),
    canComplete: z.boolean(),
    canReopen: z.boolean(),
    canDelete: z.boolean(),
  })
  .strict();
export type ShiftbookEntryPermissions = z.infer<typeof ShiftbookEntryPermissionsSchema>;

export const ShiftbookHistoryResponsiblePartyValueSchema = z
  .object({
    kind: z.enum(['user', 'group']),
    id: z.string().uuid(),
    displayName: z.string(),
  })
  .strict();
export type ShiftbookHistoryResponsiblePartyValue = z.infer<
  typeof ShiftbookHistoryResponsiblePartyValueSchema
>;

export const ShiftbookHistoryChangeValueSchema = z
  .union([z.string(), ShiftbookHistoryResponsiblePartyValueSchema])
  .nullable();
export type ShiftbookHistoryChangeValue = z.infer<typeof ShiftbookHistoryChangeValueSchema>;

export const ShiftbookHistoryChangeSchema = z.object({
  field: z.string(),
  before: ShiftbookHistoryChangeValueSchema,
  after: ShiftbookHistoryChangeValueSchema,
});
export type ShiftbookHistoryChange = z.infer<typeof ShiftbookHistoryChangeSchema>;

export const ShiftbookHistoryResponseSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  actor: ShiftbookEntryActorSchema,
  timestamp: z.string().datetime(),
  details: z.string().optional(),
  eventType: z.string().optional(),
  changes: z.array(ShiftbookHistoryChangeSchema).optional(),
});
export type ShiftbookHistoryResponse = z.infer<typeof ShiftbookHistoryResponseSchema>;

export const ShiftbookEntryDetailSchema = ShiftbookEntryListItemSchema.extend({
  description: z.string(),
  createdBy: ShiftbookEntryActorSchema,
  createdViaActor: ShiftbookEntryCreatedViaActorSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  version: z.number().int().positive(),
  keywords: z.array(ShiftbookKeywordSelectionSchema),
  attachments: z.array(AttachmentMetadataSchema),
  checklist: z.array(ShiftbookChecklistItemResponseSchema),
  comments: z.array(ShiftbookCommentResponseSchema),
  history: z.array(ShiftbookHistoryResponseSchema),
  permissions: ShiftbookEntryPermissionsSchema,
  confidential: z.boolean(),
});
export type ShiftbookEntryDetail = z.infer<typeof ShiftbookEntryDetailSchema>;
